import React, { Fragment, useEffect, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { LinkIconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { Anchor, Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	ConvoAIErrorMessage,
	ConvoAIResponseState,
	type ConvoAIStreamError,
} from '@atlassian/ai-agent-streaming';

import { useProductDependencies } from '../../../../controllers/use-product-dependencies';
import { AIErrorText } from '../ai-error-text';
import { type AIErrorType, ConvoAIErrorMessageMap } from '../ai-error-text/types';

import { messages } from './messages';

const styles = cssMap({
	anchor: {
		color: token('color.text.subtlest'),
		textDecoration: 'none',
		'&:visited': {
			color: token('color.text.subtlest'),
		},
	},
	successContainer: {
		marginTop: token('space.050'),
	},
	errorText: {
		color: token('color.text.danger'),
	},
	errorContainer: {
		color: token('color.text.danger'),
		marginTop: token('space.050'),
	},
});

const ErrorMessage = ({
	errorType,
	analyticsSource,
}: {
	analyticsSource: string;
	errorType: AIErrorType;
}) => (
	<Inline space="space.050" xcss={styles.errorContainer}>
		<Box paddingBlockStart="space.050">
			<StatusErrorIcon label="" LEGACY_size="small" color="var(--ds-icon-accent-red)" />
		</Box>
		<AIErrorText errorType={errorType} source={analyticsSource} xcss={styles.errorText} />
	</Inline>
);

export const expectedErrorTypes: Array<ConvoAIErrorMessageMap> = [
	ConvoAIErrorMessageMap.NOT_ENOUGH_CONTENT_ERROR,
	ConvoAIErrorMessageMap.ANSWER_UNAVAILABLE_FOR_CONTENT,
	ConvoAIErrorMessageMap.ACCEPTABLE_USE_VIOLATIONS,
];

type AIDescriptionStatusMessageProps = {
	analyticsSource: string;
	error?: ConvoAIStreamError;
	responseState: ConvoAIResponseState;
};

export const AIDescriptionStatusMessage = ({
	responseState,
	error,
	analyticsSource,
}: AIDescriptionStatusMessageProps): React.JSX.Element | null | undefined => {
	const { formatMessage } = useIntl();
	const { aiDescriptionExperienceTracker, errorDisplayComponent } = useProductDependencies();
	const { markErrorAsHandled } = useProductDependencies();
	const [showError, setShowError] = useState<boolean | undefined>();

	const ErrorDisplay = errorDisplayComponent || Fragment;

	useEffect(() => {
		if (responseState === ConvoAIResponseState.Error) {
			setShowError(true);

			const timer = setTimeout(() => {
				setShowError(false);
			}, 8000);

			return () => clearTimeout(timer);
		}
	}, [responseState]);

	switch (responseState) {
		case ConvoAIResponseState.Error:
			if (showError) {
				const errorType = error?.message.message_template || ConvoAIErrorMessage.UNEXPECTED;
				const mappedErrorMessage =
					errorType in ConvoAIErrorMessageMap
						? ConvoAIErrorMessageMap[errorType as keyof typeof ConvoAIErrorMessageMap]
						: ConvoAIErrorMessageMap.UNEXPECTED;
				const isExpectedError = expectedErrorTypes.includes(mappedErrorMessage);

				if (isExpectedError) {
					markErrorAsHandled?.(new Error(error?.message.content));
					aiDescriptionExperienceTracker?.abort?.({
						reason: `Cannot generate summary due to ${errorType}`,
					});
				} else {
					aiDescriptionExperienceTracker?.stopOnError({
						error: new Error(error?.message.content),
					});
				}

				return (
					<>
						{isExpectedError ? (
							<ErrorMessage errorType={errorType} analyticsSource={analyticsSource} />
						) : (
							<ErrorDisplay error={new Error(error?.message.content)}>
								<ErrorMessage errorType={errorType} analyticsSource={analyticsSource} />
							</ErrorDisplay>
						)}
					</>
				);
			}
			return null;
		case ConvoAIResponseState.FinalResponse:
			aiDescriptionExperienceTracker?.succeed();
			return (
				<Inline xcss={styles.successContainer} spread="space-between">
					<Inline space="space.050" alignBlock="center">
						<StatusInformationIcon label="" color={token('color.icon.subtle')} size="small" />
						<Anchor
							href="https://www.atlassian.com/trust/atlassian-intelligence"
							target="_blank"
							xcss={styles.anchor}
						>
							{formatMessage(messages.aIDisclaimer)}
						</Anchor>
					</Inline>
					<LinkIconButton
						spacing="compact"
						href="https://support.atlassian.com/organization-administration/docs/understand-atlassian-intelligence-features-in-products/"
						appearance="subtle"
						icon={AtlassianIntelligenceIcon}
						label={formatMessage(messages.aIAttributionLabel)}
						target="_blank"
					/>
				</Inline>
			);
		default:
			break;
	}
};
